'use client';

import { API_ROUTES } from '@shared/api-utils';
import { useFetchGames } from '@shared/hooks';
import { Vip } from '@shared/icons';
import { twMerge } from '@shared/themes';
import { useClientConfig } from '@shared/utils';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import { type ComponentProps, useEffect, useRef } from 'react';
import { CategoryDetailsButton } from '../../CategoryDetailsButton/CategoryDetailsButton';
import type { GamesCategoryFilter } from '../../games/types/types';
import { Carousel, JackpotContainer, JackpotHeader, Jackpots, Title } from '../components';
import { useFetchJackpots } from '../hooks';
import {
  type JackpotRapidFireApiEntry,
  type JackpotRapidFireProviderResponse,
  type JackpotRapidFireTierName,
  type JackpotType,
  type JackpotVariant,
  RAPID_FIRE_DISPLAY_ORDER,
} from '../types';

const TRANSLATIONS_NAMESPACE = 'casino.jackpot_rapidfire';
const JACKPOT_REFRESH_INTERVAL = 180_000;

const RAPID_FIRE_LOGO_URL = 'https://media.ffycdn.net/eu/lottoland/8YJCwR8rKtESbPB2GkK2.png';

const RAPID_FIRE_TIER_I18N_KEY: Record<JackpotRapidFireTierName, string> = {
  rapidhit2: 'master',
  rapidhit3: 'major',
  rapidhit1: 'mega_king',
  rapidhit4: 'minor',
  rapidhit5: 'mini',
};

const INITIAL_DROP_FOR_ANIMATION: Record<JackpotRapidFireTierName, number> = {
  rapidhit2: 1,
  rapidhit3: 0.75,
  rapidhit1: 15,
  rapidhit4: 0.5,
  rapidhit5: 0.01,
};

type RapidTierTotals = Partial<Record<JackpotRapidFireTierName, number>> | null | undefined;

type RapidTierPulse = Partial<
  Record<
    JackpotRapidFireTierName,
    {
      current: number;
      previous: number;
    }
  >
>;

function isBlueprintJackpotBlock(value: unknown): value is JackpotRapidFireProviderResponse {
  if (typeof value !== 'object' || value === null) return false;
  const record = value as Record<string, unknown>;
  return record.provider === 'Blueprint' && Array.isArray(record.jackpots);
}

function parseJackpotAllResponse(body: unknown): JackpotRapidFireApiEntry[] {
  if (body === undefined || body === null) return [];
  if (Array.isArray(body)) {
    for (const item of body) {
      if (isBlueprintJackpotBlock(item)) {
        return [...item.jackpots];
      }
    }
    return [];
  }
  if (isBlueprintJackpotBlock(body)) {
    return [...body.jackpots];
  }
  return [];
}

function tallyAmountByTier(entries: JackpotRapidFireApiEntry[]): Partial<Record<JackpotRapidFireTierName, number>> {
  const totals: Partial<Record<JackpotRapidFireTierName, number>> = {};
  for (const entry of entries) {
    const key = entry.jackpotName as JackpotRapidFireTierName;
    if (RAPID_FIRE_DISPLAY_ORDER.includes(key)) {
      totals[key] = entry.amount;
    }
  }
  return totals;
}

function dedupePreserveOrderGameCodes(entries: JackpotRapidFireApiEntry[]): string[] {
  const unique: string[] = [];
  const seen = new Set<string>();
  for (const row of entries) {
    for (const code of row.games ?? []) {
      if (!seen.has(code)) {
        seen.add(code);
        unique.push(code);
      }
    }
  }
  return unique;
}

function getJackpots(current: RapidTierTotals, prior: RapidTierTotals): RapidTierPulse | null {
  if (!current || !RAPID_FIRE_DISPLAY_ORDER.some(k => current[k] != null)) {
    return null;
  }

  const keyed = RAPID_FIRE_DISPLAY_ORDER.reduce((acc, tier) => {
    const newest = current[tier];
    if (newest == null) return acc;
    const earlier = prior?.[tier];
    const isGrowing = newest > (earlier ?? 0);
    acc[tier] = {
      current: newest,
      previous: earlier != null && isGrowing ? earlier : newest - INITIAL_DROP_FOR_ANIMATION[tier],
    };
    return acc;
  }, {} as RapidTierPulse);

  return Object.keys(keyed).length > 0 ? keyed : null;
}

function mapJackpot(
  tier: JackpotRapidFireTierName,
  current: number,
  previous: number,
  translator: ReturnType<typeof useTranslations>,
  refreshIntervalMs: number,
  layoutVariant: JackpotVariant
): JackpotType {
  return {
    label: translator(RAPID_FIRE_TIER_I18N_KEY[tier]),
    value: current * 100,
    previousValue: previous * 100,
    refreshInterval: refreshIntervalMs,
    highlight: tier === 'rapidhit1',
    icon: tier === 'rapidhit1' ? <Vip size="medium" /> : undefined,
    variant: layoutVariant,
  };
}

type JackpotRapidFireProps = ComponentProps<'div'> & {
  variant?: JackpotVariant;
  hideCarousel?: boolean;
  gamesFilter: GamesCategoryFilter;
};

function JackpotRapidFire({ variant = 'default', className, hideCarousel = false, gamesFilter }: JackpotRapidFireProps) {
  const t = useTranslations(TRANSLATIONS_NAMESPACE);
  const { currency } = useClientConfig();
  const apiPath = API_ROUTES.GAMES.JACKPOT_ALL.replace(':currency', currency);
  const { data } = useFetchJackpots<unknown>(apiPath, JACKPOT_REFRESH_INTERVAL);
  const previousJackpotsRef = useRef<RapidTierTotals>(null);

  const blueprintRows = parseJackpotAllResponse(data);

  useEffect(() => {
    const rows = parseJackpotAllResponse(data);
    const tallies = rows.length > 0 ? tallyAmountByTier(rows) : {};
    if (Object.keys(tallies).length === 0) return;
    previousJackpotsRef.current = tallies;
  }, [data]);

  const latestTotals = tallyAmountByTier(blueprintRows);
  const blueprintPresent = blueprintRows.length > 0 && RAPID_FIRE_DISPLAY_ORDER.some(tier => latestTotals[tier] != null);

  // eslint-disable-next-line react-hooks/refs
  const tierPulse = blueprintPresent ? getJackpots(latestTotals, previousJackpotsRef.current) : null;

  const mappedJackpots: JackpotType[] = tierPulse
    ? RAPID_FIRE_DISPLAY_ORDER.filter(tier => tierPulse[tier]).map(tier =>
        mapJackpot(tier, tierPulse[tier]!.current, tierPulse[tier]!.previous, t, JACKPOT_REFRESH_INTERVAL, variant)
      )
    : [];

  const carouselAllowList = dedupePreserveOrderGameCodes(blueprintRows);

  const { games } = useFetchGames({ ...gamesFilter, isLoggedIn: false });

  const filteredGames = games?.filter(slot => carouselAllowList.includes(slot.name)) ?? [];

  return (
    <JackpotContainer variant={variant} className={className} data-track-container="Jackpot Rapid Fire" data-testid="jackpot-rapidfire-section">
      <JackpotHeader variant={variant} className="gap-y-2">
        <Title variant={variant} namespace={TRANSLATIONS_NAMESPACE} className="text-accent-orange sr-only" />
        <Image
          src={RAPID_FIRE_LOGO_URL}
          alt={t('image_alt')}
          unoptimized
          width={438}
          height={228}
          className={twMerge('mx-auto object-contain', variant === 'default' ? 'w-100' : 'w-50')}
        />
        <Jackpots layout="rapidfire" jackpots={mappedJackpots} variant={variant} />
      </JackpotHeader>
      {variant === 'default' && !hideCarousel ? (
        <Carousel
          namespace={TRANSLATIONS_NAMESPACE}
          games={filteredGames}
          carouselTitle={t('carousel_title')}
          btnMore={
            <CategoryDetailsButton
              namespace="casino"
              category="jackpots_games"
              buttonVariant="primary"
              linkTextOverride={t('view_all')}
              filters={{ gameProviders: ['blueprint'] }}
            />
          }
        />
      ) : null}
    </JackpotContainer>
  );
}

export { getJackpots, JackpotRapidFire, mapJackpot };
